import { useMemo, useState } from 'react';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Page, PageHeader } from '@hana/shell';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { norm } from '../finance/view';
import { useArSource } from './useArSource';
import { arChipFilter, arRows, arSummary, type ArChip } from './ar';
import { DEB_ROWS, debFilter } from './deb';
import { DebitTable } from './Debit';
import { MatchQueue } from './Match';
import { ReceiptsTable } from './Receipts';
import { ArItem, CHIPS, ty } from './ArCards';
import './arpad.i18n';
import s from './arm.module.css';

// Mint-AR-iPad (1180): tab phẳng 5 mục · tìm · chip lọc · danh sách thẻ khách. TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.
type Tab = 'cus' | 'deb' | 'agt' | 'mat' | 'rcp';
const TABS: readonly (readonly [Tab, string])[] = [['cus', 'ad.tCus'], ['deb', 'ad.tDeb'], ['agt', 'ad.tAgt'], ['mat', 'ad.tMat'], ['rcp', 'ad.tRcp']];

export function ArPad() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [tab, setTab] = useState<Tab>('cus');
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<ArChip>('all');
  const noWrite = () => toast({ msg: t('ad.noWrite') });
  const src = useArSource();
  const all = useMemo(() => arRows(src).sort((a, b) => b.over - a.over), [src]);
  const rows = useMemo(() => {
    const n = norm(q.trim());
    return arChipFilter(all, chip).filter((r) => !n || norm(`${r.customer} ${r.sale}`).includes(n));
  }, [all, chip, q]);
  const sum = arSummary(rows);
  const mask = (x: string) => (access.money ? x : '•••••');
  const count = (c: ArChip) => arChipFilter(all, c).length;
  const debRows = useMemo(() => debFilter(DEB_ROWS, q, norm, false), [q]);

  return (
    <Page kind="table">
      <PageHeader
        title={t('ad.title')}
        findHint={false}
        tabs={
          <div className={s.tabs} role="tablist" aria-label={t('ad.tabsAria')}>
            {TABS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(l)}</button>)}
          </div>
        }
        actions={<Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('ad.pay')}</Button>}
      />
      <div className={s.page}>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          <section className={s.card} aria-label={t('ad.listAria')}>
            <div className={s.pg}>
              <label className={s.search}>
                <IconSearch size={16} aria-hidden="true" />
                <input aria-label={t('ad.findAria')} placeholder={t('ad.findPh')} value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
            </div>
            {tab === 'cus' && (
              <>
                <div className={`${s.chips} ${s.pg}`} style={{ paddingTop: 0 }}>
                  {CHIPS.map(([k, l]) => <button key={k} type="button" aria-pressed={chip === k} onClick={() => setChip(k)}>{t(l)} <span>{count(k)}</span></button>)}
                </div>
                <div className={s.sumRow}><span>{t('ad.rows', { n: rows.length })}</span><span>{t('adp.rec')} <b>{mask(ty(sum.total))}</b> · {t('adp.over')} <b className={s.err}>{mask(ty(sum.over))}</b></span></div>
                {rows.length === 0 ? <StateView state="empty" title={t('ad.emptyCusT')} desc={t('ad.emptyCusD')} /> : (
                  <ul className={s.list}>
                    {rows.map((r) => <li key={r.customer}><ArItem r={r} money={access.money} onOpen={() => toast({ msg: t('ad.quickView') })} /></li>)}
                  </ul>
                )}
              </>
            )}
            {tab === 'deb' && (debRows.length === 0 ? <StateView state="empty" title={t('ad.emptyDebT')} desc={t('ad.emptyDebD')} /> : <DebitTable rows={debRows} money={access.money} />)}
            {tab === 'agt' && <StateView state="empty" title={t('ad.agtT')} desc={t('ad.agtD')} />}
            {tab === 'mat' && <MatchQueue money={access.money} />}
            {tab === 'rcp' && <ReceiptsTable money={access.money} q={q} />}
          </section>
        </StateView>
      </div>
    </Page>
  );
}
